import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Trophy, Target, Lightning, Gift, Path, Flame, 
    Clock, CheckCircle, Star, CaretRight 
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import ChallengeComplete from '../components/ChallengeComplete';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DIFFICULTY_COLORS = {
    rookie: 'bg-neon-green/15 text-neon-green border border-neon-green/20',
    rider: 'bg-primary/15 text-primary border border-primary/20',
    veteran: 'bg-hot-pink/15 text-hot-pink border border-hot-pink/20',
    legend: 'bg-neon-gold/15 text-neon-gold border border-neon-gold/20',
};

const CATEGORY_ICONS = {
    distance: Path,
    speed: Lightning,
    streak: Flame,
    airdrop: Gift,
    endurance: Clock,
    ar: Star,
    community: Target,
    default: Target,
};

export default function Challenges() {
    const [allChallenges, setAllChallenges] = useState([]);
    const [myChallenges, setMyChallenges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(null);
    const [claiming, setClaiming] = useState(null);
    const [completionData, setCompletionData] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [allRes, mineRes] = await Promise.all([
                fetch(`${API}/challenges`, { credentials: 'include' }),
                fetch(`${API}/challenges/mine`, { credentials: 'include' })
            ]);

            if (allRes.ok) setAllChallenges(await allRes.json());
            if (mineRes.ok) setMyChallenges(await mineRes.json());
        } catch (error) {
            console.error("Challenges fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const joinChallenge = async (challengeId) => {
        setJoining(challengeId);
        try {
            const res = await fetch(`${API}/challenges/${challengeId}/join`, {
                method: 'POST',
                credentials: 'include'
            });

            if (res.ok) {
                toast.success('Challenge joined!');
                fetchData(); // Refresh data
            } else {
                const error = await res.json();
                toast.error(error.detail || 'Failed to join challenge');
            }
        } catch (error) {
            toast.error('Failed to join challenge');
        } finally {
            setJoining(null);
        }
    };

    const claimReward = async (challengeId) => {
        setClaiming(challengeId);
        try {
            const res = await fetch(`${API}/challenges/${challengeId}/claim`, {
                method: 'POST',
                credentials: 'include'
            });

            if (res.ok) {
                const result = await res.json();
                // Find the challenge details for the completion screen
                const challenge = allChallenges.find(c => c.challenge_id === challengeId) || 
                    myChallenges.find(c => c.challenge_id === challengeId)?.challenge;
                if (challenge) {
                    setCompletionData({
                        challenge,
                        reward: { rmr: challenge.reward_boltz, xp: challenge.reward_xp }
                    });
                }
                toast.success(result.message);
                fetchData();
            } else {
                const error = await res.json();
                toast.error(error.detail || 'Failed to claim reward');
            }
        } catch (error) {
            toast.error('Failed to claim reward');
        } finally {
            setClaiming(null);
        }
    };

    const isJoined = (challengeId) => {
        return myChallenges.some(c => c.challenge_id === challengeId);
    };

    const getJoinedChallenge = (challengeId) => {
        return myChallenges.find(c => c.challenge_id === challengeId);
    };

    const ChallengeCard = ({ challenge, showJoinButton = true }) => {
        const Icon = CATEGORY_ICONS[challenge.category] || CATEGORY_ICONS.default;
        const joined = getJoinedChallenge(challenge.challenge_id);
        const progress = joined ? (joined.progress / challenge.requirement) * 100 : 0;
        const isCompleted = joined?.status === 'completed';
        const isClaimed = joined?.status === 'claimed';

        return (
            <Card className={`glass-card ${isCompleted ? 'border-neon-green/30 box-glow' : ''}`}>
                <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isCompleted ? 'bg-neon-green/15' : 'bg-primary/10'
                        }`}>
                            {isCompleted ? (
                                <CheckCircle size={24} className="text-neon-green" weight="fill" />
                            ) : (
                                <Icon size={24} className="text-primary" />
                            )}
                        </div>
                        <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-bold">{challenge.title}</h3>
                                        <Badge className={`${DIFFICULTY_COLORS[challenge.difficulty]} text-xs`}>
                                            {challenge.difficulty}
                                        </Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {challenge.description}
                                    </p>
                                </div>
                            </div>

                            {/* Progress bar for joined challenges */}
                            {joined && !isClaimed && (
                                <div className="mt-3">
                                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                        <span>{joined.progress?.toFixed(1)} / {challenge.requirement} {challenge.requirement_unit}</span>
                                        <span>{Math.min(100, progress).toFixed(0)}%</span>
                                    </div>
                                    <Progress value={Math.min(100, progress)} className="h-2" />
                                </div>
                            )}

                            {/* Reward and action */}
                            <div className="mt-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-mono font-bold text-primary">
                                        +{challenge.reward_boltz} Boltz
                                    </span>
                                    {challenge.reward_xp > 0 && (
                                        <span className="text-sm text-muted-foreground">
                                            +{challenge.reward_xp} XP
                                        </span>
                                    )}
                                </div>

                                {showJoinButton && !joined && (
                                    <Button
                                        size="sm"
                                        onClick={() => joinChallenge(challenge.challenge_id)}
                                        disabled={joining === challenge.challenge_id}
                                        className="btn-primary text-sm"
                                        data-testid={`join-challenge-${challenge.challenge_id}`}
                                    >
                                        {joining === challenge.challenge_id ? 'Joining...' : 'Join'}
                                    </Button>
                                )}

                                {isCompleted && !isClaimed && (
                                    <Button
                                        size="sm"
                                        onClick={() => claimReward(challenge.challenge_id)}
                                        disabled={claiming === challenge.challenge_id}
                                        className="bg-neon-green hover:bg-neon-green/80 text-black text-sm font-display tracking-wider"
                                        data-testid={`claim-challenge-${challenge.challenge_id}`}
                                    >
                                        {claiming === challenge.challenge_id ? 'Claiming...' : 'Claim Reward'}
                                    </Button>
                                )}

                                {isClaimed && (
                                    <Badge variant="outline" className="text-neon-green border-neon-green/30">
                                        <CheckCircle size={14} className="mr-1" />
                                        Claimed
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6"
                >
                    <span className="label-text text-primary text-glow">CHALLENGES</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground">Complete & Earn</h1>
                    <p className="text-muted-foreground mt-1" style={{ fontFamily: 'var(--font-body)' }}>
                        Join challenges to earn bonus Boltz and XP
                    </p>
                </motion.div>

                <Tabs defaultValue="available" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-6">
                        <TabsTrigger value="available" data-testid="tab-available">
                            Available ({allChallenges.filter(c => !isJoined(c.challenge_id)).length})
                        </TabsTrigger>
                        <TabsTrigger value="active" data-testid="tab-active">
                            My Challenges ({myChallenges.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="available">
                        {loading ? (
                            <div className="space-y-4">
                                {[1, 2, 3].map((i) => (
                                    <Card key={i} className="glass-card">
                                        <CardContent className="p-4">
                                            <Skeleton className="h-24 w-full" />
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="space-y-4"
                            >
                                {allChallenges
                                    .filter(c => !isJoined(c.challenge_id))
                                    .map((challenge, index) => (
                                        <motion.div
                                            key={challenge.challenge_id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                        >
                                            <ChallengeCard challenge={challenge} />
                                        </motion.div>
                                    ))}
                                {allChallenges.filter(c => !isJoined(c.challenge_id)).length === 0 && (
                                    <Card className="glass-card">
                                        <CardContent className="p-8 text-center">
                                            <Trophy size={48} className="text-muted-foreground mx-auto mb-4" />
                                            <p className="font-medium">All challenges joined!</p>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                Complete your active challenges to earn rewards
                                            </p>
                                        </CardContent>
                                    </Card>
                                )}
                            </motion.div>
                        )}
                    </TabsContent>

                    <TabsContent value="active">
                        {loading ? (
                            <div className="space-y-4">
                                {[1, 2].map((i) => (
                                    <Card key={i} className="glass-card">
                                        <CardContent className="p-4">
                                            <Skeleton className="h-24 w-full" />
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="space-y-4"
                            >
                                {myChallenges.map((uc, index) => (
                                    <motion.div
                                        key={uc.user_challenge_id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <ChallengeCard 
                                            challenge={uc.challenge} 
                                            showJoinButton={false}
                                        />
                                    </motion.div>
                                ))}
                                {myChallenges.length === 0 && (
                                    <Card className="glass-card">
                                        <CardContent className="p-8 text-center">
                                            <Target size={48} className="text-muted-foreground mx-auto mb-4" />
                                            <p className="font-medium">No active challenges</p>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                Join challenges from the Available tab
                                            </p>
                                        </CardContent>
                                    </Card>
                                )}
                            </motion.div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>

            {/* Challenge Completion Modal */}
            {completionData && (
                <ChallengeComplete
                    challenge={completionData.challenge}
                    reward={completionData.reward}
                    onClose={() => setCompletionData(null)}
                />
            )}
        </Layout>
    );
}
